import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
  Badge,
  Alert,
} from "reactstrap";
import moment from "moment";
import HeadersRole from "../../../Components/Headers/HeadersRole";
import { fetchServicesStatus } from "../../../slices/admin/thunk";
import adminIcon from "../../../assets/images/iconsRoles/adminIcon.svg";

const ServicesStatus = () => {
  const dispatch = useDispatch();
  const [statusData, setStatusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchStatus = async () => {
    try {
      setError(null);
      const response = await dispatch(fetchServicesStatus()).unwrap();
      setStatusData(response);
    } catch (err) {
      setError(err.message || "Failed to fetch services status");
      console.error("Error fetching services status:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchStatus();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'success';
      case 'degraded':
        return 'warning';
      case 'down':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return 'ri-checkbox-circle-fill';
      case 'degraded':
        return 'ri-error-warning-fill';
      case 'down':
        return 'ri-close-circle-fill';
      default:
        return 'ri-question-fill';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'healthy':
        return 'Healthy';
      case 'degraded':
        return 'Degraded';
      case 'down':
        return 'Down';
      default:
        return 'Unknown';
    }
  };

  const headerLabel = (
    <span className="text-muted">
      Monitor external services health status
    </span>
  );

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center py-5">
            <Spinner color="primary" size="lg" />
            <p className="mt-3 mb-0">Checking services status...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <HeadersRole
        role={"Services Status"}
        icon={adminIcon}
        svg={true}
        text={headerLabel}
      />

      <Container fluid>
        {error && (
          <Alert color="danger" className="mb-4">
            <i className="ri-error-warning-line me-2"></i>
            {error}
          </Alert>
        )}

        {/* Overall Status Card */}
        {statusData && (
          <Row className="mb-4">
            <Col>
              <Card className={`border-${getStatusColor(statusData.overall)}`}>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <div className={`avatar-sm me-3`}>
                        <span className={`avatar-title bg-${getStatusColor(statusData.overall)} rounded-circle fs-2`}>
                          <i className={`${getStatusIcon(statusData.overall)} text-white`}></i>
                        </span>
                      </div>
                      <div>
                        <h5 className="mb-0">Overall System Status</h5>
                        <Badge color={getStatusColor(statusData.overall)} className="fs-6 mt-1">
                          {getStatusLabel(statusData.overall).toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-end">
                      <button
                        className={`btn btn-soft-primary btn-sm ${refreshing ? 'disabled' : ''}`}
                        onClick={handleRefresh}
                        disabled={refreshing}
                      >
                        {refreshing ? (
                          <Spinner size="sm" color="primary" className="me-1" />
                        ) : (
                          <i className="ri-refresh-line me-1"></i>
                        )}
                        Refresh
                      </button>
                      {statusData.checkedAt && (
                        <small className="text-muted d-block mt-2">
                          Last checked: {moment(statusData.checkedAt).format('MMM D, YYYY h:mm:ss A')}
                        </small>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* Individual Services */}
        {statusData?.services && (
          <Row>
            {Object.entries(statusData.services).map(([key, service]) => (
              <Col md={4} key={key}>
                <Card className="card-animate h-100">
                  <CardBody>
                    <div className="d-flex align-items-center mb-3">
                      <div className={`avatar-sm me-3`}>
                        <span className={`avatar-title bg-soft-${getStatusColor(service.status)} text-${getStatusColor(service.status)} rounded fs-3`}>
                          <i className={getStatusIcon(service.status)}></i>
                        </span>
                      </div>
                      <div className="flex-grow-1">
                        <h5 className="mb-0">{service.name}</h5>
                        <small className="text-muted text-uppercase">{key}</small>
                      </div>
                      <Badge color={getStatusColor(service.status)} className="fs-6">
                        {getStatusLabel(service.status)}
                      </Badge>
                    </div>

                    <div className="border-top pt-3">
                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted">Latency:</span>
                        <strong>
                          {service.latencyMs !== null ? `${service.latencyMs} ms` : 'N/A'}
                        </strong>
                      </div>
                      <div className="mb-2">
                        <span className="text-muted">Message:</span>
                        <p className={`mb-0 mt-1 small ${service.status === 'down' ? 'text-danger' : ''}`}>
                          {service.message}
                        </p>
                      </div>
                      {service.checkedAt && (
                        <small className="text-muted">
                          Checked: {moment(service.checkedAt).format('h:mm:ss A')}
                        </small>
                      )}
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {/* Status Legend */}
        <Row className="mt-4">
          <Col>
            <Card>
              <CardBody>
                <h6 className="card-title mb-3">Status Legend</h6>
                <div className="d-flex gap-4 flex-wrap">
                  <div className="d-flex align-items-center">
                    <Badge color="success" className="me-2">Healthy</Badge>
                    <small className="text-muted">Service responding normally</small>
                  </div>
                  <div className="d-flex align-items-center">
                    <Badge color="warning" className="me-2">Degraded</Badge>
                    <small className="text-muted">Service responding with issues</small>
                  </div>
                  <div className="d-flex align-items-center">
                    <Badge color="danger" className="me-2">Down</Badge>
                    <small className="text-muted">Service not responding (timeout/error)</small>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ServicesStatus;
